import { isDeepEqual } from '@attain-apps/data-utils'
import * as store from '@attain-apps/fe/web/main/lib/store'
import * as rxjs from 'rxjs'

import type * as variableOverrides from '../../../../../../variable-overrides'
import type * as aliases from '../../../../../aliases'
import type * as currentRendering from '../../../feature'
import type { Feature } from '../feature'

/**
 * Debounce time for variable requests. Used to avoid sending multiple requests
 * if variables are changed in quick succession.
 */
const VARIABLE_DEBOUNCE_TIME = 500

/**
 * Triggers the rendering process for the widgets on the given rendering (page)
 */
export const triggerWidgetsRenderings = store.effects.create(
  (
    feature: Feature,
    currentRenderingFeature: currentRendering.Feature,
    variablesFeature: variableOverrides.Feature,
    aliasFeature: aliases.Feature,
  ) =>
    rxjs
      .merge(
        // We listen for when the page starts and finishes rendering. When it
        // starts rendering we stop all widget rendering requests, and when it
        // finishes rendering we start all widget rendering requests. We tie
        // widget renderings to the page renderings in this way to avoid
        // unnecessary widget renderings in response to all the changes that
        // happen during page render
        currentRenderingFeature.actions.render.request.observe(),
        currentRenderingFeature.actions.render.success.observe(),
      )
      .pipe(
        rxjs.switchMap(action =>
          // if we are in the middle of the request
          action.op.status === 'request'
            ? // cancel inner observable(s)
              rxjs.EMPTY
            : // if we are done with the request we start the widget rendering
              // requests and maintaining the stream open util the next request
              rxjs
                .combineLatest({
                  allWidgetRefs: feature.selectors.allWidgetRefs.asObservable,
                  pageContext:
                    currentRenderingFeature.selectors.context.asObservable,
                })
                .pipe(
                  rxjs.switchMap(({ pageContext, allWidgetRefs }) =>
                    pageContext === undefined || allWidgetRefs.length === 0
                      ? // Cannot render widgets without the page context
                        rxjs.EMPTY
                      : // spread widget refs to get all widgets for the given container
                        rxjs.from(allWidgetRefs).pipe(
                          rxjs.mergeMap(widgetRef =>
                            aliasFeature.selectors
                              // Convert widget flexibleIdentifier (ID or alias) to its actual widget ID
                              .getWidgetId(widgetRef.widget)
                              .asObservable.pipe(
                                rxjs.switchMap(
                                  // base don the widget id try obtaining widget
                                  // variables
                                  widgetId =>
                                    variablesFeature.selectors.getVariableOverrides(
                                      widgetId
                                        ? {
                                            widget: {
                                              appId: pageContext.appId,
                                              pageId: pageContext.pageId,
                                              refId: widgetRef.id,
                                              scenarioId:
                                                pageContext.scenarioId,
                                              widgetId,
                                            },
                                          }
                                        : // in case widget id is undefined, get
                                          // at last other variables (page,
                                          // session etc...)
                                          undefined,
                                    ).asObservable,
                                ),
                                rxjs.debounceTime(VARIABLE_DEBOUNCE_TIME),
                                // even thought the above selectors should
                                // deduplicate most requests there is edge
                                // case where we start with no widget id, then
                                // get the widget id, but end up with the same
                                // variables
                                rxjs.distinctUntilChanged(isDeepEqual),
                                // Prepare collected data into object with
                                // required data for rendering
                                rxjs.map(widgetVariables => ({
                                  pageContext,
                                  widgetRef,
                                  widgetVariables,
                                })),
                              ),
                          ),
                        ),
                  ),
                  rxjs.map(({ pageContext, widgetRef, widgetVariables }) =>
                    feature.actions.render
                      .from('triggerWidgetsRenderings (effect)')
                      .create({
                        pageContext,
                        ref: widgetRef,
                        variables: {
                          ...widgetRef.variables,
                          ...widgetVariables,
                        },
                      }),
                  ),
                ),
        ),
      ),
)
